// CRM - 신고된 게시글 / 댓글 관리 페이지

import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FlexBox from 'components/flexbox/FlexBox';
import { H4, H5, Small } from 'components/Typography';
import {
  AlertTriangle,
  Check,
  Clock,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import CrmCommentsSection from 'page-sections/crm/CrmCommentsSection';
import MenuPanel from 'page-sections/crm/MenuPanel';
import { useState } from 'react';
import { customShadows } from 'theme/shadows';

export default function ReportedPostsAndComments() {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isLaptop = useMediaQuery(theme.breakpoints.down('lg'));

  const [leftPanelWidth] = useState(1.5);
  const [boardFilter, setBoardFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [authorSearch, setAuthorSearch] = useState('');

  const customStyle = {
    width: '100%',
    overflow: 'hidden',
    backgroundColor: '#fafafa',
  };

  // 임시 고정 데이터 (디자인 재현용)
  const totalComments = 15;
  const totalOnlyComments = 6;
  const totalReplies = 9;
  const pendingCount = 3;
  const reportedCount = 3;

  const dropdownMenuProps = {
    PaperProps: {
      sx: {
        mt: 1,
        borderRadius: '20px',
        boxShadow: customShadows.card,
        minWidth: 220,
        '& .MuiMenuItem-root': {
          fontSize: '14px',
          paddingY: 1,
          paddingX: 2.5,
          color: '#4B4F5C',
        },
        '& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover':
          {
            color: theme.palette.primary.mainBlue,
            backgroundColor: 'transparent',
            fontWeight: 700,
          },
      },
    },
  };

  return (
    <Box sx={customStyle}>
      <Grid container alignItems={'flex-start'}>
        <Grid
          item
          xs={12}
          container
          direction="row"
          alignItems="flex-start"
          bgcolor={'#ffffff'}
          height={'100%'}
          borderRight={`1px solid #E1E1E1`}
        >
          {/* 왼쪽 패널 */}
          <Grid
            item
            lg={leftPanelWidth}
            xs={12}
            order={isLaptop ? 2 : 0}
            display={'flex'}
            flexDirection={'row'}
            sx={{ overflow: 'hidden' }}
            height={'100%'}
          >
            <MenuPanel />
          </Grid>

          {/* 중간부분 */}
          <Grid
            container
            item
            lg={12 - leftPanelWidth}
            xs={12}
            bgcolor={'#fafafa'}
            borderLeft={`1px solid #E1E1E1`}
          >
            <Grid item lg={12} xs={12}>
              <Box
                pt={3}
                pb={3}
                sx={{
                  borderBottom: `1px solid #E1E1E1`,
                  overflow: 'auto',
                }}
              >
                <Box
                  px={isTablet ? 2 : 4}
                  display="flex"
                  flexDirection="column"
                  gap={3}
                >
                  {/* 상단 통계 카드 */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                      <Card
                        sx={{
                          padding: '18px 20px',
                          borderRadius: '16px',
                          boxShadow: customShadows.card,
                          backgroundColor: '#ffffff',
                          border: 'none',
                          height: '100%',
                        }}
                      >
                        <Small
                          sx={{ color: '#808187', mb: 0.5, display: 'block' }}
                        >
                          총 댓글 / 답글
                        </Small>
                        <H4 sx={{ fontWeight: 700, color: '#151515' }}>
                          {totalComments}
                        </H4>
                        <Small
                          sx={{
                            color: '#A0A4B0',
                            mt: 1,
                            display: 'block',
                          }}
                        >
                          오늘 + 12개
                        </Small>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                      <Card
                        sx={{
                          padding: '18px 20px',
                          borderRadius: '16px',
                          boxShadow: customShadows.card,
                          backgroundColor: '#ffffff',
                          border: 'none',
                          height: '100%',
                        }}
                      >
                        <Small
                          sx={{ color: '#808187', mb: 0.5, display: 'block' }}
                        >
                          댓글
                        </Small>
                        <H4 sx={{ fontWeight: 700, color: '#151515' }}>
                          {totalOnlyComments}
                        </H4>
                        <Small
                          sx={{
                            color: '#A0A4B0',
                            mt: 1,
                            display: 'block',
                          }}
                        >
                          메인 댓글
                        </Small>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                      <Card
                        sx={{
                          padding: '18px 20px',
                          borderRadius: '16px',
                          boxShadow: customShadows.card,
                          backgroundColor: '#ffffff',
                          border: 'none',
                          height: '100%',
                        }}
                      >
                        <Small
                          sx={{ color: '#808187', mb: 0.5, display: 'block' }}
                        >
                          답글
                        </Small>
                        <H4 sx={{ fontWeight: 700, color: '#151515' }}>
                          {totalReplies}
                        </H4>
                        <Small
                          sx={{
                            color: '#A0A4B0',
                            mt: 1,
                            display: 'block',
                          }}
                        >
                          댓글의 답글
                        </Small>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                      <Card
                        sx={{
                          padding: '18px 20px',
                          borderRadius: '16px',
                          boxShadow: customShadows.card,
                          backgroundColor: '#ffffff',
                          border: 'none',
                          height: '100%',
                        }}
                      >
                        <FlexBox alignItems="center" gap={1}>
                          <Clock size={18} color="#FF9B26" />
                          <Small
                            sx={{ color: '#151515', mb: 0.5, display: 'block' }}
                          >
                            승인 대기
                          </Small>
                        </FlexBox>
                        <H4 sx={{ fontWeight: 700, color: '#FF9B26' }}>
                          {pendingCount}
                        </H4>
                        <Small
                          sx={{
                            color: '#FF9B26',
                            mt: 1,
                            display: 'block',
                            fontWeight: 600,
                          }}
                        >
                          확인 필요
                        </Small>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                      <Card
                        sx={{
                          padding: '18px 20px',
                          borderRadius: '16px',
                          boxShadow: customShadows.card,
                          backgroundColor: '#ffffff',
                          border: 'none',
                          height: '100%',
                        }}
                      >
                        <FlexBox alignItems="center" gap={1}>
                          <AlertTriangle
                            size={18}
                            color={theme.palette.primary.red}
                          />
                          <Small
                            sx={{
                              color: '#151515',
                              mb: 0.5,
                              display: 'block',
                            }}
                          >
                            신고됨
                          </Small>
                        </FlexBox>
                        <H4
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.primary.red,
                          }}
                        >
                          {reportedCount}
                        </H4>
                        <Small
                          sx={{
                            color: theme.palette.primary.red,
                            mt: 1,
                            display: 'block',
                            fontWeight: 600,
                          }}
                        >
                          긴급 조치
                        </Small>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* 검색 필터 영역 */}
                  <Card
                    sx={{
                      padding: '18px 20px',
                      borderRadius: '24px',
                      boxShadow: customShadows.card,
                      backgroundColor: '#ffffff',
                      border: 'none',
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid item xs={12} md={8.5}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={3}>
                            <Small
                              sx={{
                                color: '#5D6473',
                                mb: 1,
                                display: 'block',
                              }}
                            >
                              게시판
                            </Small>
                            <FormControl fullWidth size="small">
                              <Select
                                value={boardFilter}
                                onChange={e => setBoardFilter(e.target.value)}
                                displayEmpty
                                MenuProps={dropdownMenuProps}
                                sx={{
                                  backgroundColor: '#F5F5F7',
                                  borderRadius: '999px',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                    {
                                      border: 'none',
                                    },
                                  fontSize: '14px',
                                  px: 2,
                                }}
                              >
                                <MenuItem value="all">전체 게시판</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Small
                              sx={{
                                color: '#5D6473',
                                mb: 1,
                                display: 'block',
                              }}
                            >
                              유형
                            </Small>
                            <FormControl fullWidth size="small">
                              <Select
                                value={typeFilter}
                                onChange={e => setTypeFilter(e.target.value)}
                                displayEmpty
                                MenuProps={dropdownMenuProps}
                                sx={{
                                  backgroundColor: '#F5F5F7',
                                  borderRadius: '999px',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                    {
                                      border: 'none',
                                    },
                                  fontSize: '14px',
                                  px: 2,
                                }}
                              >
                                <MenuItem value="all">
                                  전체 (댓글+답글)
                                </MenuItem>
                                <MenuItem value="comment">댓글만</MenuItem>
                                <MenuItem value="reply">답글만</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Small
                              sx={{
                                color: '#5D6473',
                                mb: 1,
                                display: 'block',
                              }}
                            >
                              상태
                            </Small>
                            <FormControl fullWidth size="small">
                              <Select
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                                displayEmpty
                                MenuProps={dropdownMenuProps}
                                sx={{
                                  backgroundColor: '#F5F5F7',
                                  borderRadius: '999px',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                    {
                                      border: 'none',
                                    },
                                  fontSize: '14px',
                                  px: 2,
                                }}
                              >
                                <MenuItem value="all">전체 상태</MenuItem>
                                <MenuItem value="approved">승인 완료</MenuItem>
                                <MenuItem value="pending">승인 대기</MenuItem>
                                <MenuItem value="reported">신고됨</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Small
                              sx={{
                                color: '#5D6473',
                                mb: 1,
                                display: 'block',
                              }}
                            >
                              작성자 (닉네임/이름)
                            </Small>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="작성자 검색"
                              value={authorSearch}
                              onChange={e => setAuthorSearch(e.target.value)}
                              InputProps={{
                                sx: {
                                  backgroundColor: '#F5F5F7',
                                  borderRadius: '999px',
                                  '& fieldset': { border: 'none' },
                                  fontSize: '14px',
                                  px: 2,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={3.5}
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: 1,
                          mt: { xs: 1.5, md: 0 },
                        }}
                      >
                        <Button
                          variant="contained"
                          startIcon={<Search size={18} />}
                          sx={{
                            backgroundColor: theme.palette.primary.mainBlue,
                            color: '#ffffff',
                            borderRadius: '12px',
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: 'none',
                            '& .MuiButton-startIcon': {
                              marginRight: 1,
                            },
                            '&:hover': {
                              backgroundColor:
                                theme.palette.primary.mainBlueHover,
                              boxShadow: 'none',
                            },
                          }}
                        >
                          검색
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<RefreshCw size={18} />}
                          sx={{
                            borderRadius: '12px',
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: 500,
                            textTransform: 'none',
                            backgroundColor: theme.palette.primary.grey10,
                            borderColor: '#E4E7EC',
                            color: '#7A7F8C',
                            '& .MuiButton-startIcon': {
                              marginRight: 1,
                            },
                            '&:hover': {
                              borderColor: '#CBD0DA',
                            },
                          }}
                        >
                          초기화
                        </Button>
                      </Grid>
                    </Grid>
                  </Card>

                  {/* 댓글 / 답글 목록 영역 */}
                  <Card
                    sx={{
                      padding: '18px 20px 8px',
                      borderRadius: '18px',
                      boxShadow: customShadows.card,
                      backgroundColor: '#ffffff',
                      border: 'none',
                    }}
                  >
                    <FlexBox
                      justifyContent={isTablet ? 'flex-start' : 'space-between'}
                      alignItems={isTablet ? 'flex-start' : 'center'}
                      flexDirection={isTablet ? 'column' : 'row'}
                      mb={2}
                      gap={isTablet ? 1 : 0}
                    >
                      <FlexBox alignItems="center" gap={1.2}>
                        <H5 sx={{ fontWeight: 600, color: '#151515' }}>
                          댓글 / 답글 목록 ({totalComments}개)
                        </H5>
                      </FlexBox>
                      <FlexBox alignItems="center" gap={1}>
                        <Button
                          variant="contained"
                          startIcon={<Check size={18} />}
                          sx={{
                            backgroundColor: '#06C755',
                            color: '#ffffff',
                            borderRadius: '12px',
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: 'none',
                            '& .MuiButton-startIcon': {
                              marginRight: 1,
                            },
                            '&:hover': {
                              backgroundColor: '#05B24D',
                              boxShadow: 'none',
                            },
                          }}
                        >
                          선택 승인
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<Trash2 size={18} />}
                          sx={{
                            borderRadius: '12px',
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: 500,
                            textTransform: 'none',
                            backgroundColor: theme.palette.primary.grey10,
                            borderColor: '#E4E7EC',
                            color: '#7A7F8C',
                            '& .MuiButton-startIcon': {
                              marginRight: 1,
                            },
                            '&:hover': {
                              borderColor: '#CBD0DA',
                            },
                          }}
                        >
                          선택 삭제
                        </Button>
                      </FlexBox>
                    </FlexBox>

                    <FlexBox alignItems="center" gap={1} mb={0}>
                      <Checkbox size="small" />
                      <Small sx={{ color: '#4B4F5C', fontSize: '14px' }}>
                        전체 선택
                      </Small>
                    </FlexBox>

                    <Box>
                      <CrmCommentsSection
                        isTablet={isTablet}
                        isMobile={isTablet}
                      />
                    </Box>
                  </Card>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
